import { type FieldConfig, useField } from 'formik';
import { type FocusEvent, forwardRef, type Ref } from 'react';

import { useTranslation } from '@yoweb/next-i18next';
import InputUI, { type InputProps } from './InputUI';
import { toHalfWidth, toFullWidth } from '@yoweb/utils/intl/charWidth';
import { useIntlMeta } from '@yoweb/ui/hooks/useIntlMeta';

type FormInputProps = Omit<InputProps, 'error'> &
  FieldConfig &
  DataTestId & { validatePasswordStrength?: boolean };

/**
 * An Formik controlled Input component.
 */
function Input(
  { enforceCharacterWidth, validatePasswordStrength = true, ...props }: FormInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const [field, meta] = useField(props);
  const { t } = useTranslation<'common'>();
  const { localeHasFullWidthChars } = useIntlMeta();
  const success =
    validatePasswordStrength && props.type === 'password' && meta.touched && !meta.error
      ? t('validation.password-strength-strong')
      : undefined;

  /**
   * Components that use Input might have their own event handlers
   * we need to call both them and the formik event handlers.
   */
  const onChange = (event: FocusEvent<HTMLInputElement>) => {
    if (enforceCharacterWidth && localeHasFullWidthChars) {
      if (enforceCharacterWidth === 'half') {
        event.currentTarget.value = toHalfWidth(event.currentTarget.value);
      } else {
        event.currentTarget.value = toFullWidth(event.currentTarget.value);
      }
    }

    props.onChange?.(event);
    field.onChange(event);
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    props.onBlur?.(event);
    field.onBlur(event);
  };

  return (
    <InputUI
      ref={ref}
      {...field}
      {...props}
      onChange={onChange}
      onBlur={onBlur}
      error={meta.touched ? meta.error : undefined}
      success={success}
    />
  );
}

export default forwardRef(Input);
